// RBAC — Positions-Ränge + Nutzerverwaltung/Tarif-Logik. Reine, I/O-freie Domänenlogik.
// Die eigentlichen Rechte-Prüfungen laufen über die V2-Rechte-Tokens (packages/shared/src/permissions.ts);
// die alte Cap-Maschinerie (ALL_CAPS/isCap/cleanCaps/userCaps) + die einmalige Cutover-Migration sind nach
// dem Prod-Lauf entfernt. Hier bleibt nur, was rein an Position/Rang hängt (Rang-Floor, isBoss, canManageUser).

/** Positionen mit Hierarchie-Rang (höher = mehr Befugnis). Super-Admin steht über allem. */
export const RANK: Record<string, number> = {
  Chef: 100,
  Zweitchef: 90,
  Werkstattmeister: 70,
  Serviceannahme: 50,
  Verkauf: 45,
  Einkauf: 40,
  Mechaniker: 20,
  Mitarbeiter: 10,
}
export const DEFAULT_RANK = 10
export const SUPERADMIN_RANK = 9999

export interface UserLike {
  /** V2-Rechte-Tokens als JSON-String (DB) ODER Array (Domäne). Hier nur strukturell geführt; die Rang-Logik
   *  unten liest es nicht — Rechte-Prüfungen laufen über permissions.ts. */
  permissions?: string | readonly string[] | null
  position?: string | null
  /** boolean (Domäne) oder 0/1-Integer (DB-Zeile) — beides wird toleriert. */
  isSuperadmin?: boolean | number | null
}

export function isSuperadmin(user: UserLike | null | undefined): boolean {
  return !!(user && user.isSuperadmin)
}

/**
 * Zeitkorrekturen sind BEWUSST positionsbasiert (nur Chef/Zweitchef), nicht cap-basiert:
 * die 'chef'-Cap hängt auch an Meister/Annahme/Verkauf, die hier nichts zu suchen haben (Altcode R-03).
 */
export function isBoss(user: UserLike | null | undefined): boolean {
  return !!(user && (user.position === 'Chef' || user.position === 'Zweitchef'))
}

export function rankOfPosition(position: string | null | undefined): number {
  return position != null && RANK[position] != null ? (RANK[position] as number) : DEFAULT_RANK
}

export function userRank(user: UserLike | null | undefined): number {
  if (isSuperadmin(user)) return SUPERADMIN_RANK
  return rankOfPosition(user?.position ?? null)
}

/**
 * Darf `actor` den `target`-Nutzer verwalten (anlegen/ändern/abschalten/löschen)?
 * Regel: strikt höherer Rang. Der Super-Admin ist unantastbar (nie verwaltbar), darf aber selbst alles.
 */
export function canManageUser(actor: UserLike | null | undefined, target: UserLike | null | undefined): boolean {
  if (isSuperadmin(target)) return false
  if (isSuperadmin(actor)) return true
  return userRank(actor) > userRank(target)
}

/** Maximale aktive Nutzer (ohne Superadmin) je Tarif — 1:1 Alt-Parität PLAN_USER_LIMIT. */
export const PLAN_USER_LIMIT: Record<string, number> = {
  test: 2,
  solo: 1,
  team: 5,
  werkstatt: 9999,
  owner: 9999,
  probe: 9999,
}

/** Nutzerlimit für einen Tarif. Unbekannter/fehlender Plan → Test-Limit (2), wie im Altcode. */
export function userLimitForPlan(plan: string | null | undefined): number {
  return (plan != null && PLAN_USER_LIMIT[plan] != null ? PLAN_USER_LIMIT[plan] : PLAN_USER_LIMIT.test) as number
}

/**
 * Gültige Tarif-Keys (= die Schlüssel von PLAN_USER_LIMIT). Die EINE Wahrheit, gegen die jeder
 * Admin-Schreibpfad einen Plan validieren MUSS: `userLimitForPlan` fällt bei einem unbekannten String
 * STILL auf das Test-Limit (2) zurück — ein Tippfehler ('Team' statt 'team') würde sonst eine zahlende
 * Werkstatt klammheimlich auf 2 Seats kappen. Validieren statt frei Text akzeptieren.
 */
export const KNOWN_PLANS: readonly string[] = Object.keys(PLAN_USER_LIMIT)
export function isKnownPlan(plan: string | null | undefined): plan is string {
  return typeof plan === 'string' && Object.prototype.hasOwnProperty.call(PLAN_USER_LIMIT, plan)
}
